import { createContext, memo, useContext, useMemo } from 'react';
import { IntegrationsQueries } from '@frontend/api-integrations';

type InsuranceReportContainerProviderProps = {
  locationId: string;
  sourceId: string;
  children?: React.ReactNode;
};

type InsuranceReportContainerProviderState = {
  PMSName: string;
  isDocumentCenterWritebackSupported: boolean;
  isInsuranceDetailsWritebackSupported: boolean;
  isLoading: boolean;
};

export const InsuranceReportContainerProviderContext = createContext<InsuranceReportContainerProviderState>({
  PMSName: '',
  isDocumentCenterWritebackSupported: false,
  isInsuranceDetailsWritebackSupported: false,
  isLoading: false,
});

export const InsuranceReportContainerProvider = memo(
  ({ locationId, sourceId, children }: InsuranceReportContainerProviderProps) => {
    const {
      isDocumentCenterWritebackSupported,
      PMSName,
      isLoading: isSyncAppDataLoading,
      isInsuranceDetailsWritebackSupported,
    } = IntegrationsQueries.useIsWritebackSupported({
      locationId,
      sourceId: sourceId || '',
    });

    const providerValue = useMemo(
      () => ({
        PMSName,
        isDocumentCenterWritebackSupported,
        isInsuranceDetailsWritebackSupported,
        isLoading: isSyncAppDataLoading,
      }),
      [isDocumentCenterWritebackSupported, PMSName, isSyncAppDataLoading, isInsuranceDetailsWritebackSupported]
    );

    return (
      <InsuranceReportContainerProviderContext.Provider value={providerValue}>
        {children}
      </InsuranceReportContainerProviderContext.Provider>
    );
  }
);

export const useInsuranceReportContainerProviderContext = () => {
  const context = useContext(InsuranceReportContainerProviderContext);
  if (!context) {
    throw new Error(
      'useInsuranceReportContainerProviderContext must be used within an InsuranceReportContainerProviderContext'
    );
  }
  return context;
};

InsuranceReportContainerProvider.displayName = 'InsuranceReportContainerProvider';
